import React from 'react';
import { motion } from 'framer-motion';
import { Github, Chrome } from 'lucide-react';
import { useAuth } from '../../contexts/SupabaseAuthContext';

interface OAuthButtonsProps {
  loading?: boolean;
  onLoading?: (loading: boolean) => void;
  onError?: (error: string) => void;
}

export const OAuthButtons: React.FC<OAuthButtonsProps> = ({ 
  loading = false, 
  onLoading,
  onError 
}) => {
  const { signInWithOAuth } = useAuth();

  const handleOAuthLogin = async (provider: 'google' | 'github') => {
    try {
      onLoading?.(true);
      await signInWithOAuth!(provider);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'OAuth login failed';
      onError?.(message);
    } finally {
      onLoading?.(false);
    }
  };

  const oauthProviders = [
    {
      id: 'google' as const,
      name: 'Google',
      icon: Chrome,
      color: 'from-red-500 to-orange-500',
      hoverColor: 'from-red-600 to-orange-600'
    },
    {
      id: 'github' as const,
      name: 'GitHub',
      icon: Github,
      color: 'from-gray-800 to-gray-900',
      hoverColor: 'from-gray-900 to-black'
    }
  ];

  return (
    <div className="space-y-3">
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-white text-gray-500">Or continue with</span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {oauthProviders.map((provider) => {
          const IconComponent = provider.icon;
          return (
            <motion.button
              key={provider.id}
              type="button"
              onClick={() => handleOAuthLogin(provider.id)}
              disabled={loading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-xl bg-gradient-to-r ${provider.color} hover:${provider.hoverColor} text-white font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <IconComponent className="w-5 h-5 mr-3" />
              {loading ? 'Connecting...' : `Continue with ${provider.name}`}
            </motion.button>
          );
        })}
      </div>

      <div className="text-center">
        <p className="text-xs text-gray-500 px-4">
          By continuing, you agree to our Terms of Service and Privacy Policy. 
          Your account will be created automatically if it doesn't exist.
        </p>
      </div>
    </div>
  );
};